import React, {ReactNode} from "react";
import {Theme} from "@mui/material";
import {COLORADO_TEMPLATE} from "./templates/colorado/template";
import {CALIFORNIA_TEMPLATE} from "./templates/california/template";
import {FLORIDA_TEMPLATE} from "./templates/florida/template";
import {OREGON_TEMPLATE} from "./templates/oregon/template";
import {FormGen} from "@/shared/formgen";
import {JsonObject} from "@/shared/json/json-object";
import {JsonProperty} from "@/shared/json/json-property";
import {$KTM, $KTS, KeyMetadatas, KeyTextStrings} from "@/shared/types";

// Image imports
import alignersImage from "./res/images/services/aligners.jpg";
import dentureImage from "./res/images/services/denture.jpg";
import emergencyImage from "./res/images/services/emergency.jpg";
import generalImage from "./res/images/services/general.jpg";
import implantsImage from "./res/images/services/implants.jpg";
import veneersImage from "./res/images/services/veneers.jpg";
import whiteningImage from "./res/images/services/whitening.jpg";

export type PageComponentProps<T extends BaseSectionData> = { template: SiteTemplate, children?: ReactNode, data: T };

export type SiteTemplatePageSections = {
  AddressSection: React.ComponentType<PageComponentProps<AddressSectionData>>,
  ContactSection: React.ComponentType<PageComponentProps<ContactSectionData>>,
  FaqSection: React.ComponentType<PageComponentProps<FaqSectionData>>,
  FooterSection: React.ComponentType<PageComponentProps<FooterSectionData>>,
  GallerySection: React.ComponentType<PageComponentProps<GallerySectionData>>,
  HeaderSection: React.ComponentType<PageComponentProps<HeaderSectionData>>,
  HeroSection: React.ComponentType<PageComponentProps<HeroSectionData>>,
  HighlightSection: React.ComponentType<PageComponentProps<HighlightSectionData>>,
  PromoSection: React.ComponentType<PageComponentProps<PromoSectionData>>,
  ServicesSection: React.ComponentType<PageComponentProps<ServicesSectionData>>,
  StatsSection: React.ComponentType<PageComponentProps<StatsSectionData>>,
  TeamSection: React.ComponentType<PageComponentProps<TeamSectionData>>,
  TestimonialsSection: React.ComponentType<PageComponentProps<TestimonialsSectionData>>,
}

export type SiteTemplate = {
  name: string,
  text: string,
  thumbnailUrl?: string,
  theme: Theme,
  themeExtras: {
    highlightColor: string,
  },
  PageSections: SiteTemplatePageSections,
}

export const TEMPLATES: SiteTemplate[] = [
  COLORADO_TEMPLATE,
  CALIFORNIA_TEMPLATE,
  FLORIDA_TEMPLATE,
  OREGON_TEMPLATE,
];

@JsonObject()
export class BaseSectionData {

  protected static _from<T extends BaseSectionData>(target: T, json: any): T {
    return Object.assign(target, json);
  }
}

@JsonObject()
export class Link extends BaseSectionData {

  @FormGen({
    name: "Text",
    type: "string",
  })
  @JsonProperty()
  text: string;

  @FormGen({
    name: "URL",
    type: "string",
  })
  @JsonProperty()
  url: string;

  static from(json: any): Link {
    return this._from(new Link(), json);
  }
}

@JsonObject()
export class Feature extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Text",
    type: "string",
  })
  @JsonProperty()
  text: string;

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string;

  @FormGen({
    name: "Text",
    type: "string",
  })
  @JsonProperty()
  url: string;

  static from(json: any): Feature {
    return this._from(new Feature(), json);
  }
}

@JsonObject()
export class FooterSectionData extends BaseSectionData {

  @FormGen({
    name: "Logo",
    type: "profile_photo",
  })
  @JsonProperty()
  logo: string;

  @FormGen({
    name: "Name",
    type: "string",
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Tagline",
    type: "string",
  })
  @JsonProperty()
  tagline: string;

  @FormGen({
    name: "Links",
    type: "array_object",
    arrayObjectCreator: () => new Link(),
  })
  @JsonProperty({dataStructure: "array", type: Link})
  readonly links: Link[] = [];

  @FormGen({
    name: "Copyright",
    type: "string",
  })
  @JsonProperty()
  copyrightText: string;

  static from(json: any): FooterSectionData {
    return this._from(new FooterSectionData(), json);
  }
}

@JsonObject()
export class HeaderSectionData extends BaseSectionData {

  @FormGen({
    name: "Logo",
    type: "profile_photo",
  })
  @JsonProperty()
  logo: string;

  @FormGen({
    name: "Name",
    type: "string",
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Phone",
    type: "string",
  })
  @JsonProperty()
  phone: string;

  @FormGen({
    name: "Links",
    type: "array_object",
    arrayObjectCreator: () => new Link(),
  })
  @JsonProperty({dataStructure: "array", type: Link})
  readonly links: Link[] = [];

  static from(json: any): HeaderSectionData {
    return this._from(new HeaderSectionData(), json);
  }
}

@JsonObject()
export class OpeningHours extends BaseSectionData {

  @FormGen({
    name: "Days",
    type: "string",
  })
  @JsonProperty()
  days: string = "";

  @FormGen({
    name: "Hours",
    type: "string",
  })
  @JsonProperty()
  hours: string = "";

  static from(json: any): OpeningHours {
    return this._from(new OpeningHours(), json);
  }
}

@JsonObject()
export class AddressSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Address line 1",
    type: "string",
  })
  @JsonProperty()
  addressLine1: string;

  @FormGen({
    name: "Address line 2",
    type: "string",
  })
  @JsonProperty()
  addressLine2: string;

  @FormGen({
    name: "Phone",
    type: "string",
  })
  @JsonProperty()
  phone: string;

  @FormGen({
    name: "Email",
    type: "email",
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Opening hours",
    type: "array_object",
    arrayObjectCreator: () => new OpeningHours(),
  })
  @JsonProperty({dataStructure: "array", type: OpeningHours})
  readonly openingHours: OpeningHours[] = [];

  @FormGen({
    name: "Map embed URL",
    type: "string",
    description: "Paste the src URL from a Google Maps embed iframe.",
  })
  @JsonProperty()
  mapEmbedUrl: string;

  static from(json: any): AddressSectionData {
    return this._from(new AddressSectionData(), json);
  }
}

@JsonObject()
export class ContactSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Subtitle",
    type: "string",
  })
  @JsonProperty()
  subtitle: string;

  static from(json: any): ContactSectionData {
    return this._from(new ContactSectionData(), json);
  }
}

export class FaqItem extends BaseSectionData {

  @FormGen({
    name: "Question",
    type: "string",
  })
  @JsonProperty()
  question: string;

  @FormGen({
    name: "Answer",
    type: "text",
  })
  @JsonProperty()
  answer: string;

  static from(json: any): FaqItem {
    return this._from(new FaqItem(), json);
  }
}

@JsonObject()
export class FaqSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Subtitle",
    type: "string",
  })
  @JsonProperty()
  subtitle: string;

  @FormGen({
    name: "FAQs",
    type: "array_object",
    arrayObjectCreator: () => new FaqItem(),
  })
  @JsonProperty()
  readonly faqs: FaqItem[] = [];

  static from(json: any): FaqSectionData {
    return this._from(new FaqSectionData(), json);
  }
}

@JsonObject()
export class GalleryPhoto extends BaseSectionData {

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string;

  @FormGen({
    name: "Caption",
    type: "string",
  })
  @JsonProperty()
  caption: string = "";

  static from(json: any): GalleryPhoto {
    return this._from(new GalleryPhoto(), json);
  }
}

@JsonObject()
export class GallerySectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Photos",
    type: "array_object",
    arrayObjectCreator: () => new GalleryPhoto(),
  })
  @JsonProperty()
  readonly photos: GalleryPhoto[] = [];

  static from(json: any): GallerySectionData {
    return this._from(new GallerySectionData(), json);
  }
}

@JsonObject()
export class HeroSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string;

  @FormGen({
    name: "Primary link",
    type: "object",
  })
  @JsonProperty()
  primaryLink: Link = new Link();

  @FormGen({
    name: "Secondary link",
    type: "object",
  })
  @JsonProperty()
  secondaryLink: Link = new Link();

  static from(json: any): HeroSectionData {
    return this._from(new HeroSectionData(), json);
  }
}

@JsonObject()
export class HighlightSectionData extends BaseSectionData {

  @FormGen({
    name: "Eyebrow",
    type: "string",
  })
  @JsonProperty()
  eyebrow: string;

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Text",
    type: "text",
  })
  @JsonProperty()
  text: string;

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string;

  @FormGen({
    name: "Links",
    type: "array_object",
    arrayObjectCreator: () => new Link(),
  })
  @JsonProperty({dataStructure: "array", type: Link})
  readonly links: Link[] = [];

  @FormGen({
    name: "Image position",
    type: "enum",
    enumValues: [{key: "left", text: "Left"}, {key: "right", text: "Right"}],
  })
  @JsonProperty()
  imagePosition: "left" | "right" = "left";

  static from(json: any): HighlightSectionData {
    return this._from(new HighlightSectionData(), json);
  }
}

@JsonObject()
export class PromoSectionData extends BaseSectionData {

  @FormGen({
    name: "Eyebrow",
    type: "string",
  })
  @JsonProperty()
  eyebrow: string;

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Body",
    type: "text",
  })
  @JsonProperty()
  body: string;

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string;

  @FormGen({
    name: "Link",
    type: "object",
  })
  @JsonProperty({dataStructure: "array", type: Link})
  link: Link = new Link();

  @FormGen({
    name: "Image position",
    type: "enum",
    enumValues: [{key: "left", text: "Left"}, {key: "right", text: "Right"}],
  })
  @JsonProperty()
  imagePosition: "left" | "right" = "right";

  static from(json: any): PromoSectionData {
    return this._from(new PromoSectionData(), json);
  }
}

export enum ServiceType {
  ALIGNERS = "aligners",
  DENTURES = "dentures",
  EMERGENCY = "emergency",
  GENERAL = "general",
  IMPLANTS = "implants",
  VENEERS = "veneers",
  WHITENING = "whitening",
}

type ServiceTypeMetadata = {
  imageUrl: string,
}

export const SERVICE_TYPE_KTM = new KeyMetadatas<ServiceTypeMetadata>([
  // Hack to make it work across both CRA and Nextjs
  $KTM(ServiceType.ALIGNERS, {imageUrl: alignersImage["src"] || alignersImage as unknown as string}),
  $KTM(ServiceType.DENTURES, {imageUrl: dentureImage["src"] || dentureImage as unknown as string}),
  $KTM(ServiceType.EMERGENCY, {imageUrl: emergencyImage["src"] || emergencyImage as unknown as string}),
  $KTM(ServiceType.GENERAL, {imageUrl: generalImage["src"] || generalImage as unknown as string}),
  $KTM(ServiceType.IMPLANTS, {imageUrl: implantsImage["src"] || implantsImage as unknown as string}),
  $KTM(ServiceType.VENEERS, {imageUrl: veneersImage["src"] || veneersImage as unknown as string}),
  $KTM(ServiceType.WHITENING, {imageUrl: whiteningImage["src"] || whiteningImage as unknown as string}),
]);

@JsonObject()
export class Service extends BaseSectionData {

  @JsonProperty()
  readonly type: ServiceType;

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Description",
    type: "text",
  })
  @JsonProperty()
  description: string;

  @FormGen({
    name: "Image",
    type: "profile_photo",
  })
  @JsonProperty()
  imageUrl: string = "";

  @FormGen({
    name: "Link",
    type: "object",
  })
  @JsonProperty()
  link: Link = new Link();

  static from(json: any): Service {
    return this._from(new Service(), json);
  }
}

export enum ServicesDisplayMode {
  STANDARD = "standard",
  COMPACT = "compact",
}

const SERVICESDISPLAY_MODE_KTS = new KeyTextStrings([
  $KTS(ServicesDisplayMode.STANDARD, "Standard"),
  $KTS(ServicesDisplayMode.COMPACT, "Compact"),
]);

@JsonObject()
export class ServicesSectionData extends BaseSectionData {

  @FormGen({
    name: "Display Mode",
    type: "enum",
    enumValues: SERVICESDISPLAY_MODE_KTS.values,
    enumVariant: "radio",
  })
  @JsonProperty()
  displayMode: ServicesDisplayMode = ServicesDisplayMode.STANDARD;

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Subtitle",
    type: "string",
  })
  @JsonProperty()
  subtitle: string;

  @FormGen({
    name: "Services",
    type: "array_object",
    arrayObjectCreator: () => new Service(),
  })
  @JsonProperty({dataStructure: "array", type: Service})
  readonly services: Service[] = [];

  static from(json: any): ServicesSectionData {
    return this._from(new ServicesSectionData(), json);
  }
}

@JsonObject()
export class Stat extends BaseSectionData {

  @FormGen({
    name: "Value",
    type: "string",
  })
  @JsonProperty()
  value: string;

  @FormGen({
    name: "Label",
    type: "string",
  })
  @JsonProperty()
  label: string;

  @FormGen({
    name: "Description",
    type: "string",
  })
  @JsonProperty()
  description: string = "";

  static from(json: any): Stat {
    return this._from(new Stat(), json);
  }
}

@JsonObject()
export class StatsSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  title: string;

  @FormGen({
    name: "Stats",
    type: "array_object",
    arrayObjectCreator: () => new Stat(),
  })
  @JsonProperty({dataStructure: "array", type: Stat})
  readonly stats: Stat[] = [];

  static from(json: any): StatsSectionData {
    return this._from(new StatsSectionData(), json);
  }
}

@JsonObject()
export class TeamMember extends BaseSectionData {

  @FormGen({
    name: "Name",
    type: "string",
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Role",
    type: "string",
  })
  @JsonProperty()
  role: string;

  @FormGen({
    name: "Bio",
    type: "text",
  })
  @JsonProperty()
  bio: string;

  @FormGen({
    name: "Photo",
    type: "profile_photo",
  })
  @JsonProperty()
  photoUrl: string = "";

  static from(json: any): TeamMember {
    return this._from(new TeamMember(), json);
  }
}

@JsonObject()
export class TeamSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Subtitle",
    type: "string",
  })
  @JsonProperty()
  subtitle: string;

  @FormGen({
    name: "Members",
    type: "array_object",
    arrayObjectCreator: () => new TeamMember(),
  })
  @JsonProperty({dataStructure: "array", type: TeamMember})
  readonly members: TeamMember[] = [];

  static from(json: any): TeamSectionData {
    return this._from(new TeamSectionData(), json);
  }
}

@JsonObject()
export class Testimonial extends BaseSectionData {

  @FormGen({
    name: "Quote",
    type: "text",
  })
  @JsonProperty()
  quote: string;

  @FormGen({
    name: "Author name",
    type: "string",
  })
  @JsonProperty()
  authorName: string;

  @FormGen({
    name: "Author title",
    type: "string",
  })
  @JsonProperty()
  authorTitle: string;

  @FormGen({
    name: "Author photo",
    type: "profile_photo",
  })
  @JsonProperty()
  authorImageUrl: string = "";

  @FormGen({
    name: "Rating",
    type: "number",
    numberMin: 1,
    numberMax: 5,
    numberIntegerOnly: true,
  })
  @JsonProperty()
  rating: number = 5;

  static from(json: any): Testimonial {
    return this._from(new Testimonial(), json);
  }
}

@JsonObject()
export class TestimonialsSectionData extends BaseSectionData {

  @FormGen({
    name: "Title",
    type: "string",
  })
  @JsonProperty()
  title: string;

  @FormGen({
    name: "Testimonials",
    type: "array_object",
    arrayObjectCreator: () => new Testimonial(),
  })
  @JsonProperty({dataStructure: "array", type: Testimonial})
  readonly testimonials: Testimonial[] = [];

  static from(json: any): TestimonialsSectionData {
    return this._from(new TestimonialsSectionData(), json);
  }
}